import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";


const waterfallData = [
  { name: "Memberships", value: 48200, type: "source", color: "#0F766E" },
  { name: "Personal Training", value: 18500, type: "source", color: "#0D9488" },
  { name: "POS / Retail", value: 9800, type: "source", color: "#14B8A6" },
  { name: "Supplements", value: 5400, type: "source", color: "#2DD4BF" },
  { name: "Pending", value: -12300, type: "deduction", color: "#DC2626" },
  { name: "Refunds", value: -2100, type: "deduction", color: "#F97316" },
  { name: "Net Revenue", value: 67500, type: "total", color: "#0F766E" },
];

const paymentMethodData = [
  { method: "Credit Card", amount: 32400, pct: 48 },
  { method: "Cash", amount: 18900, pct: 28 },
  { method: "Bank Transfer", amount: 10800, pct: 16 },
  { method: "UPI / Digital", amount: 5400, pct: 8 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    const val = payload?.[0]?.value;
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg">
        <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">{label}</p>
        <p className={`text-sm font-bold mt-1 ${val < 0 ? "text-red-500" : "text-[var(--color-primary)]"}`}>
          {val < 0 ? "-" : ""}${Math.abs(val)?.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

export default function RevenueWaterfallChart() {
  const [activeView, setActiveView] = useState("waterfall");

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-4 lg:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="text-base lg:text-lg font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Revenue Breakdown</h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">By source, deductions & net</p>
        </div>
        <div className="flex items-center gap-2 bg-[var(--color-muted)] rounded-lg p-1">
          {["waterfall", "payment"]?.map((v) => (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium font-[var(--font-caption)] transition-all duration-200 ${activeView === v ? "bg-[var(--color-card)] text-[var(--color-primary)] shadow-sm" : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"}`}
            >
              {v === "waterfall" ? "By Source" : "By Method"}
            </button>
          ))}
        </div>
      </div>
      {activeView === "waterfall" ? (
        <div className="w-full h-64 lg:h-80" aria-label="Revenue Waterfall Chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={waterfallData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.abs(v / 1000)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {waterfallData?.map((entry, index) => (
                  <Cell key={index} fill={entry?.color} opacity={entry?.type === "deduction" ? 0.85 : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="space-y-3">
          {paymentMethodData?.map((item) => (
            <div key={item?.method} className="flex items-center gap-3">
              <span className="text-sm font-[var(--font-caption)] text-[var(--color-foreground)] w-28 flex-shrink-0">{item?.method}</span>
              <div className="flex-1 bg-[var(--color-muted)] rounded-full h-3">
                <div className="h-3 rounded-full bg-[var(--color-primary)] transition-all duration-500" style={{ width: `${item?.pct}%` }} />
              </div>
              <span className="text-sm font-bold font-[var(--font-data)] text-[var(--color-foreground)] w-20 text-right">${item?.amount?.toLocaleString()}</span>
              <span className="text-xs text-[var(--color-muted-foreground)] w-8 text-right">{item?.pct}%</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-[var(--color-border)]">
        {[
          { label: "Revenue Sources", color: "#0F766E" },
          { label: "Deductions", color: "#DC2626" },
          { label: "Net Total", color: "#0F766E" },
        ]?.map((item) => (
          <div key={item?.label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item?.color }} />
            <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{item?.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}